/**
 * Goal card dropdown menu — edit, archive, announce.
 * Uses portal to avoid overflow clipping.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { MoreHorizontal, Pencil, Trash2, Megaphone } from 'lucide-react'

interface GoalCardMenuProps {
  onEdit: () => void
  onDelete: () => void
  onAnnounce?: () => void
  isAnnounced: boolean
}

export const GoalCardMenu: React.FC<GoalCardMenuProps> = ({
  onEdit, onDelete, onAnnounce, isAnnounced,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const btnRef = useRef<HTMLButtonElement>(null)

  const toggle = useCallback((): void => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setPos({ top: rect.bottom + 4, left: rect.right - 160 })
    }
    setOpen((p) => !p)
  }, [open])

  // Close on outside click (bubble phase, not capture)
  useEffect(() => {
    if (!open) return
    const handleOutsideClick = (e: MouseEvent): void => {
      // Don't close if clicking inside the dropdown
      const target = e.target as HTMLElement
      if (target.closest('[data-goal-menu]')) return
      setOpen(false)
    }
    // Use setTimeout to avoid the same click that opened the menu
    const timer = setTimeout(() => {
      document.addEventListener('click', handleOutsideClick)
    }, 0)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [open])

  const items = [
    { icon: <Pencil size={14} />, label: t('goals.editGoal'), action: onEdit, danger: false },
    ...(!isAnnounced && onAnnounce ? [{ icon: <Megaphone size={14} />, label: t('goals.announce'), action: onAnnounce, danger: false }] : []),
    { icon: <Trash2 size={14} />, label: t('goals.delete'), action: onDelete, danger: true },
  ]

  return (
    <>
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); toggle() }}
        className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/[0.06] transition-colors tap-highlight-transparent"
      >
        <MoreHorizontal size={16} className="text-text-hint" />
      </button>

      {open && createPortal(
        <>
          {/* Invisible backdrop to catch outside clicks */}
          <div
            className="fixed inset-0 z-[79]"
            onClick={(e) => { e.stopPropagation(); setOpen(false) }}
          />
          <div
            data-goal-menu
            className="fixed z-[80] min-w-[160px] py-1 rounded-xl bg-surface border border-border shadow-xl shadow-black/40"
            style={{ top: pos.top, left: Math.max(8, pos.left) }}
          >
            {items.map((item) => (
              <button
                key={item.label}
                onClick={(e) => {
                  e.stopPropagation()
                  setOpen(false)
                  item.action()
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-white/[0.04] transition-colors ${
                  item.danger ? 'text-danger' : 'text-text'
                }`}
              >
                <span className="text-text-hint">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>
        </>,
        document.body,
      )}
    </>
  )
}
